/**
 * Permission Engine — Pure Functions
 * ===================================
 *
 * The engine is a set of small, synchronous, dependency-free functions
 * that answer one question each:
 *
 *   hasAnyPermission(userRoles, 'KICK_MEMBERS') -> boolean
 *   canManageUser(actorRoles, targetRoles)     -> boolean
 *   canAssignRole(actorRoles, targetRole)      -> boolean
 *   getDangerLevel(userRoles)                  -> 'safe'|'caution'|'dangerous'
 *
 * These run IDENTICALLY on the client (UI gating, button visibility)
 * and on the server (Express endpoint authorization). Server.ts also
 * double-checks via Supabase RLS, so a client-side bypass is caught.
 */

import {
  PERMISSION_ADMINISTRATOR,
  PERMISSION_DELETE_SERVER,
  PERMISSION_INDEX,
  PERMISSION_MANAGE_ROLES,
  SYSTEM_ROLE_ADMINISTRATOR,
  SYSTEM_ROLE_CREATOR,
  SYSTEM_ROLE_EVERYONE,
  SYSTEM_ROLE_HELPER,
} from './permissions';

/* ---------------------------------------------------------------- *
 * Role / Permission records (DB shape, accepts whatever the SELECTs
 * return — we only read the fields we care about).
 * ---------------------------------------------------------------- */

export interface PermissionRole {
  id: string;
  vault_id: string;
  name: string;
  color: string;
  position: number;
  role_kind: 'cosmetic' | 'permission';
  permission_ids: string[];
  is_system?: boolean;
  is_deletable?: boolean;
}

export interface CosmeticRole {
  id: string;
  vault_id: string;
  name: string;
  color: string;
  position: number;
  role_kind: 'cosmetic';
  permission_ids: string[];
  is_system?: boolean;
  is_deletable?: boolean;
  mentionable?: boolean;
}

export type AnyRole = PermissionRole | CosmeticRole;

/* ---------------------------------------------------------------- *
 * Core permission queries
 * ---------------------------------------------------------------- */

/**
 * hasPermission — does ANY of the user's roles grant the given perm?
 * The ADMINISTRATOR meta-permission is checked first because a role
 * with only ADMINISTRATOR listed must still satisfy granular perm
 * checks — we expand it to "all non-Creator-only permissions".
 *
 * Cosmetic roles NEVER grant permissions. Their permission_ids array
 * should always be empty; we explicitly reject them as a defense in
 * depth so accidentally attaching permissions to a cosmetic role
 * doesn't bleed moderation power.
 */
export function hasPermission(
  userRoles: AnyRole[] | null | undefined,
  permission: string,
): boolean {
  if (!userRoles || userRoles.length === 0) return false;

  return userRoles.some(role => {
    if (role.role_kind === 'cosmetic') return false;
    const perms: string[] = role.permission_ids ?? [];
    if (perms.includes(PERMISSION_ADMINISTRATOR)) {
      // ADMINISTRATOR = all perms EXCEPT DELETE_SERVER — UNLESS
      // DELETE_SERVER is explicitly listed alongside ADMINISTRATOR
      // (the @Creator role does exactly that, granting the meta-perm
      // plus ownership's destructive power).
      if (permission === PERMISSION_DELETE_SERVER && !perms.includes(PERMISSION_DELETE_SERVER)) return false;
      return true;
    }
    return perms.includes(permission);
  });
}

export function hasAnyPermission(
  userRoles: AnyRole[] | null | undefined,
  permissions: string[],
): boolean {
  return permissions.some(p => hasPermission(userRoles, p));
}

export function hasAllPermissions(
  userRoles: AnyRole[] | null | undefined,
  permissions: string[],
): boolean {
  return permissions.every(p => hasPermission(userRoles, p));
}

/**
 * unionOfPermissions — return every permission granted by any of
 * the user's roles, used for the live "Permission Summary" in the
 * Role Settings UI.
 */
export function unionOfPermissions(
  userRoles: AnyRole[] | null | undefined,
): string[] {
  if (!userRoles) return [];
  const set = new Set<string>();
  for (const role of userRoles) {
    if (role.role_kind === 'cosmetic') continue;
    for (const p of role.permission_ids ?? []) set.add(p);
    // ADMINISTRATOR expands to everything but DELETE_SERVER.
    if ((role.permission_ids ?? []).includes(PERMISSION_ADMINISTRATOR)) {
      for (const meta of Object.values(PERMISSION_INDEX)) {
        if (meta.id === 'DELETE_SERVER') continue;
        set.add(meta.id);
      }
    }
  }
  return Array.from(set);
}

/* ---------------------------------------------------------------- *
 * Role hierarchy
 * ---------------------------------------------------------------- */

/**
 * getTopRole — highest-rank role in the user's set (lowest position
 * number = highest rank, mirroring Discord's convention flipped).
 */
export function getTopRole(userRoles: AnyRole[] | null | undefined): PermissionRole | null {
  if (!userRoles || userRoles.length === 0) return null;
  let top: PermissionRole = userRoles[0] as PermissionRole;
  for (const role of userRoles) {
    if (role.role_kind === 'cosmetic') continue;
    if (role.position < top.position) top = role as PermissionRole;
  }
  return top;
}

/** Highest position integer (= lowest rank). null if no roles. */
export function getLowestRank(userRoles: AnyRole[] | null | undefined): number | null {
  if (!userRoles || userRoles.length === 0) return null;
  return Math.max(...userRoles.filter(r => r.role_kind === 'permission').map(r => r.position));
}

/**
 * canManageUser — actor can act on target if actor's highest rank is
 * strictly higher than the target's highest rank, AND the actor has
 * the relevant permission. The Creator target is unmanageable by anyone
 * — even another Creator cannot kick/ban them.
 *
 * Note: soft-rule layered for typical Discord behaviour; the server
 * layer is the source of truth.
 */
export function canManageUser(
  actorRoles: AnyRole[] | null | undefined,
  targetRoles: AnyRole[] | null | undefined,
  requiredPermission: string,
): boolean {
  if (!hasPermission(actorRoles, requiredPermission)) return false;
  const actorTop = getTopRole(actorRoles);
  const targetTop = getTopRole(targetRoles);
  // If the target is @Creator, no one can manage them.
  if (targetTop?.id === SYSTEM_ROLE_CREATOR) return false;
  if (!actorTop) return false;
  if (!targetTop) {
    // Target has no permission roles — they're effectively @everyone.
    // Anyone with the required permission and at least one role higher
    // than @everyone can manage them.
    return actorTop.position < 999; // every system position implied
  }
  return actorTop.position < targetTop.position;
}

/**
 * canAssignRole — can the actor grant the target role to someone?
 * Rules:
 *   1. Actor must hold MANAGE_ROLES OR ADMINISTRATOR.
 *   2. Actor cannot assign a role at or above their own highest rank.
 *   3. The @Creator system role is unassignable by anyone but the
 *      platform admin (handled via RLS bypass).
 *   4. Cosmetic roles only need MANAGE_COSMETIC_ROLES (or ADMINISTRATOR).
 */
export function canAssignRole(
  actorRoles: AnyRole[] | null | undefined,
  targetRole: AnyRole,
): boolean {
  if (targetRole.id === SYSTEM_ROLE_CREATOR) return false; // never assignable
  const actorTop = getTopRole(actorRoles);
  if (!actorTop) return false;

  if (targetRole.role_kind === 'cosmetic') {
    if (!hasPermission(actorRoles, PERMISSION_MANAGE_ROLES) &&
        !hasPermission(actorRoles, 'MANAGE_COSMETIC_ROLES')) return false;
  } else {
    if (!hasPermission(actorRoles, PERMISSION_MANAGE_ROLES)) return false;
    // Strict inequality: actor's top position must be lower than target's
    // (numerically smaller = higher rank). Equality means same-or-higher.
    if (actorTop.position >= targetRole.position) return false;
  }
  return true;
}

/**
 * canEditRole — can the actor change this role's name / permission
 * list / position? Uses the same hierarchical rules as canAssignRole
 * because editing an existing role is equivalent to assigning whatever
 * permissions end up on it. Cosmetic roles slightly relaxed (no
 * permission list to police) but still gated by rank.
 */
export function canEditRole(
  actorRoles: AnyRole[] | null | undefined,
  targetRole: AnyRole,
): boolean {
  if (targetRole.id === SYSTEM_ROLE_CREATOR) {
    // The Creator role is editable by Creator only — but is_deletable=false
    // is enforced in storage and UI hides the delete button.
    const actorTop = getTopRole(actorRoles);
    return actorTop?.id === SYSTEM_ROLE_CREATOR;
  }
  return canAssignRole(actorRoles, targetRole);
}

/**
 * canDeleteRole — the UI uses this to hide / show the delete button.
 */
export function canDeleteRole(
  actorRoles: AnyRole[] | null | undefined,
  targetRole: AnyRole,
): boolean {
  if (targetRole.is_deletable === false) return false;
  if (targetRole.id === SYSTEM_ROLE_CREATOR) return false;
  return canAssignRole(actorRoles, targetRole);
}

/**
 * filterAssignableRoles — convenience for the role-picker dropdown in
 * the Assign Role modal. Returns the subset of roles the actor is
 * permitted to grant.
 */
export function filterAssignableRoles(
  actorRoles: AnyRole[] | null | undefined,
  allRoles: AnyRole[],
): AnyRole[] {
  return allRoles.filter(r => canAssignRole(actorRoles, r));
}

/* ---------------------------------------------------------------- *
 * Audit helpers
 * ---------------------------------------------------------------- */

export interface AuditLogEntry {
  id: string;
  vault_id: string;
  actor_id: string;
  action: string;
  target_id: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export const MODERATION_ACTIONS = [
  'KICK', 'BAN', 'UNBAN', 'TIMEOUT', 'UNTIMEOUT',
  'SERVER_MUTE', 'SERVER_UNMUTE', 'SERVER_DEAFEN', 'SERVER_UNDEAFEN',
  'CHANNEL_CREATE', 'CHANNEL_DELETE', 'CHANNEL_EDIT',
  'ROLE_CREATE', 'ROLE_EDIT', 'ROLE_DELETE', 'ROLE_ASSIGN', 'ROLE_UNASSIGN',
  'VOICE_LIMIT_SET',
  'SERVER_EDIT', 'SERVER_DELETE',
] as const;

export type ModerationAction = typeof MODERATION_ACTIONS[number];

/* ---------------------------------------------------------------- *
 * Timeout presets (the UI sends these on the request body)
 * ---------------------------------------------------------------- */

export const TIMEOUT_PRESETS_SECONDS = {
  SIXTY_SECONDS: 60,
  FIVE_MINUTES: 5 * 60,
  THIRTY_MINUTES: 30 * 60,
  ONE_HOUR: 60 * 60,
  TWELVE_HOURS: 12 * 60 * 60,
  ONE_DAY: 24 * 60 * 60,
  ONE_WEEK: 7 * 24 * 60 * 60,
} as const;

export const TIMEOUT_PRESETS: Array<{ label: string; seconds: number }> = [
  { label: '60 seconds', seconds: TIMEOUT_PRESETS_SECONDS.SIXTY_SECONDS },
  { label: '5 minutes',  seconds: TIMEOUT_PRESETS_SECONDS.FIVE_MINUTES },
  { label: '30 minutes', seconds: TIMEOUT_PRESETS_SECONDS.THIRTY_MINUTES },
  { label: '1 hour',     seconds: TIMEOUT_PRESETS_SECONDS.ONE_HOUR },
  { label: '12 hours',   seconds: TIMEOUT_PRESETS_SECONDS.TWELVE_HOURS },
  { label: '1 day',      seconds: TIMEOUT_PRESETS_SECONDS.ONE_DAY },
  { label: '1 week',     seconds: TIMEOUT_PRESETS_SECONDS.ONE_WEEK },
];
